import React from 'react'
import { useSelector } from 'react-redux'
import { RootState, Subtask } from 'constants/interfaces'
import colors from 'constants/colors'
import { RadioCheckbox } from 'components/TasksV2/UI/RadioCheckbox'
import useLocaleText from 'components/useLocaleText'
import { useTaskOverviewContext } from '../../TaskOverviewStackProvider'
import { useWorkspaceText } from '../../hooks/useWorkspaceText'
import { AnswerDraft, findExecutionResult } from '../../model/execution'
import { getExecutionPollOptions, nextPollVotes } from '../../model/poll'
import styles from './styles.module.css'

const pollLiterals = { votes: { key: 't_votes', fallback: { en: 'Votes', he: 'הצבעות' } } }

export function ExecutionPollAnswer({
  subtask,
  draft,
  onChange,
  disabled,
}: {
  subtask: Subtask
  draft: AnswerDraft
  onChange: (patch: Partial<AnswerDraft>) => void
  disabled: boolean
}) {
  const { task, resultContextId } = useTaskOverviewContext()
  const results = useSelector((state: RootState) => state.tasks_v2.allResults[subtask.tid])
  const loading = useSelector((state: RootState) => state.tasks_v2.allResultsLoading)
  const config = useSelector((state: RootState) => state.config.config)
  const t = useWorkspaceText()
  const labels = useLocaleText(pollLiterals)
  const votes = draft.votes || []
  const savedVotes =
    findExecutionResult(results || [], subtask.st_id, resultContextId, task.recurrence_ts)?.poll_selections || []
  const options = getExecutionPollOptions({
    subtask,
    results: results || [],
    contextId: resultContextId,
    recurrenceTs: task.recurrence_ts,
    votes: savedVotes,
    knownContextIds: new Set([...Object.keys(config.groups || {}), ...Object.keys(config.retailUsersObject || {})]),
  })

  return (
    <fieldset className={styles.poll} disabled={disabled || loading} aria-busy={loading}>
      <legend>
        {t.answer} <small>{subtask.is_multi_choice ? t.multiple : t.single}</small>
      </legend>
      {options.map(({ label, count, percentage }, index) => (
        <div className={styles.option} key={index}>
          <RadioCheckbox
            value={label}
            name={`answer-${subtask.st_id}`}
            inputType={subtask.is_multi_choice ? 'checkbox' : 'radio'}
            disabled={disabled || loading}
            isChecked={votes.includes(index)}
            onChange={() => onChange({ votes: nextPollVotes(votes, index, !!subtask.is_multi_choice) })}
          >
            <span className={styles.label} dir="auto">
              {label}
            </span>
            {!loading && count > 0 && (
              <span className={styles.count} aria-label={`${labels.votes}: ${count}`}>
                {count}
              </span>
            )}
          </RadioCheckbox>
          <div
            className={styles.track}
            role="progressbar"
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={loading ? undefined : percentage}
          >
            <div
              className={styles.fill}
              style={{ width: `${loading ? 0 : percentage}%`, background: colors.greenPoll }}
            />
          </div>
        </div>
      ))}
    </fieldset>
  )
}
